import * as React from 'react'
import { SlotsResponse } from '../../../types'

export type FetchSlots = (params: { from: string, to: string, timezone: string, duration: number }) => Promise<SlotsResponse>

// Fetches slots for the visible month whenever month/timezone/duration change. The window is padded by a day
// before and 15 days after (as instants) because the server groups by the booker timezone, whose local days can
// straddle the browser's month edges, and so the trailing next-month days in the calendar grid show availability.
// The fetcher is held in a ref so a host re-render with a new function identity does not refetch;
// the effect is keyed on month/timezone/duration only. Bump `refreshKey` to force a refetch
// (e.g. the editor after availability overrides change).
export default function useSlots(fetchSlots: FetchSlots, month: Date, timezone: string, duration: number, refreshKey: number | string = 0) {
  const fetchRef = React.useRef(fetchSlots)
  fetchRef.current = fetchSlots
  const [slots, setSlots] = React.useState<Record<string, string[]>>({})
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(false)
  const [reloadKey, setReloadKey] = React.useState(0)
  const year = month.getFullYear()
  const monthIndex = month.getMonth()

  React.useEffect(() => {
    let cancelled = false
    const DAY = 24 * 60 * 60 * 1000
    const from = new Date(Date.UTC(year, monthIndex, 1) - DAY)
    const to = new Date(Date.UTC(year, monthIndex + 1, 1) + 15 * DAY)
    setLoading(true)
    setError(false)
    fetchRef.current({ from: from.toISOString(), to: to.toISOString(), timezone, duration })
      .then(res => { if (!cancelled) setSlots(res.slots || {}) })
      .catch(() => { if (!cancelled) { setSlots({}); setError(true) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [year, monthIndex, timezone, duration, refreshKey, reloadKey])

  return { slots, loading, error, reload: () => setReloadKey(k => k + 1) }
}
